// @ts-strict-ignore
import { component } from "picoapp";
import {
  getFirstFocusableElement,
  getLastFocusableElement,
} from "../../utilities";
import { checkCanFocusTrap } from "../../../scripts/lib/focus-trap-utils";
import { type Context } from "../../state";
import { TablescapeEvent, TablescapeContext } from "@tablescape/context";

export const mobileNavigation = component<Context>(async (node, context) => {
  // Elements
  const topNavHamburgerElement = document.querySelector<HTMLButtonElement>(
    ".js-hamburger-button"
  );
  const topNavCloseElement =
    node.querySelector<HTMLButtonElement>(".js-close-button");
  const mobileMenu = node.querySelector<HTMLElement>(".js-mobile-menu");
  const mobileMenuHeader = node.querySelector<HTMLElement>(
    ".js-mobile-menu-header"
  );
  const mobileMenuHeaderLastFocusElement =
    getLastFocusableElement(mobileMenuHeader);

  const dropdownLevelOne = node.querySelector<HTMLElement>(
    ".js-dropdown-level-1"
  );
  const dropdownLevelOneLinkElements = node.querySelectorAll<HTMLElement>(
    ".js-dropdown-level-1-link"
  );
  const dropdownLevelTwoCloseButtonElements =
    node.querySelectorAll<HTMLButtonElement>(".js-dropdown-level-2-close");
  const bodyElement = document.querySelector("body");
  const cartButtonElements = node.querySelectorAll(".js-cart-button");

  // State
  let mobileNavOpen = false;
  let activeLevelTwoDropdown = null;
  const dropdownLevelOneLastFocusElement =
    getLastFocusableElement(dropdownLevelOne);
  let mobileNavLastFocusableElement = dropdownLevelOneLastFocusElement;

  // Handlers
  const cartClickHandler = (e) => {
    TablescapeContext.emit(TablescapeEvent.TOGGLE_CART);
    closeMobileDropdownHandler(e);
  };

  const handleMobileMenuKeyboardEvents = (e: KeyboardEvent) => {
    if (mobileNavOpen) {
      if (e.key === "Tab") {
        if (!e.shiftKey) {
          if (document.activeElement === mobileNavLastFocusableElement) {
            topNavCloseElement.focus();
            e.preventDefault();
          }

          if (document.activeElement === mobileMenuHeaderLastFocusElement) {
            const currentLevelTwoDropdown = node.querySelector<HTMLElement>(
              `#js-mobile-menu-${activeLevelTwoDropdown}`
            );
            currentLevelTwoDropdown
              ? getFirstFocusableElement(currentLevelTwoDropdown).focus()
              : getFirstFocusableElement(dropdownLevelOne).focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === topNavCloseElement) {
            mobileNavLastFocusableElement.focus();
            e.preventDefault();
          }
        }
      }

      if (e.key === "Escape") {
        if (activeLevelTwoDropdown) {
          closeMobileDropdownLevelTwoHandler(e);
        } else {
          closeMobileDropdownHandler(e);
        }
      }
    }
  };

  const openMobileDropdownHandler = async () => {
    topNavHamburgerElement.ariaExpanded = "true";
    node.classList.remove("invisible");
    node.classList.remove("opacity-0");
    node.classList.add("z-max");
    node.classList.remove("-z-10");

    mobileMenu.classList.remove("invisible");
    mobileMenu.classList.add("left-0");
    mobileMenu.classList.remove("left-full");
    mobileMenu.classList.remove("opacity-0");

    bodyElement.dataset.scrollY = window.scrollY.toString();
    bodyElement.classList.add("fixed", "overflow-y-hidden");

    try {
      await checkCanFocusTrap([node], function (trapContainer) {
        return getComputedStyle(trapContainer).visibility !== "hidden";
      });

      topNavCloseElement.focus();
      mobileNavOpen = true;
    } catch (e) {
      console.error(e);
    }
  };

  const closeMobileDropdownHandler = (e) => {
    topNavHamburgerElement.ariaExpanded = "false";
    bodyElement.classList.remove("fixed", "overflow-y-hidden");
    window.scroll({ top: parseInt(bodyElement.dataset.scrollY) });
    bodyElement.removeAttribute("data-scroll-y");
    closeMobileDropdownLevelTwoHandler(e);

    mobileMenu.classList.add("left-full");
    mobileMenu.classList.remove("left-0");
    mobileMenu.classList.add("opacity-0");
    mobileMenu.classList.add("invisible");

    node.classList.add("opacity-0");
    node.classList.add("-z-10");
    node.classList.remove("z-max");
    node.classList.add("invisible");

    topNavHamburgerElement.focus();
    mobileNavOpen = false;
  };

  const openMobileDropdownLevelTwoHandler = async (e) => {
    e.preventDefault();
    const targetId = e.currentTarget.dataset.levelTwoId;
    const dropdownLevelTwo = node.querySelector<HTMLElement>(
      `#js-mobile-menu-${targetId}`
    );
    const closeButton = dropdownLevelTwo.querySelector<HTMLElement>(
      ".js-dropdown-level-2-close"
    );

    dropdownLevelOne.classList.add("invisible");
    dropdownLevelOne.classList.add("-left-full");
    dropdownLevelOne.classList.add("opacity-0");

    dropdownLevelTwo?.classList.remove("left-full");
    dropdownLevelTwo?.classList.add("left-0");
    dropdownLevelTwo?.classList.remove("opacity-0");
    dropdownLevelTwo?.classList.remove("invisible");

    try {
      await checkCanFocusTrap([dropdownLevelTwo], function (trapContainer) {
        return (
          getComputedStyle(trapContainer).visibility !== "hidden" &&
          parseInt(getComputedStyle(trapContainer).left) === 0
        );
      });

      mobileNavLastFocusableElement = getLastFocusableElement(dropdownLevelTwo);
      closeButton.focus();
      activeLevelTwoDropdown = targetId;
    } catch (error) {
      console.error(error);
    }
  };

  const closeMobileDropdownLevelTwoHandler = async (e) => {
    e.preventDefault();
    const dropdownLevelTwo = node.querySelector<HTMLElement>(
      `#js-mobile-menu-${activeLevelTwoDropdown}`
    );

    if (!dropdownLevelTwo) return;
    dropdownLevelOne.classList.remove("invisible");
    dropdownLevelOne.classList.remove("-left-full");
    dropdownLevelOne.classList.remove("opacity-0");

    dropdownLevelTwo?.classList.remove("left-0");
    dropdownLevelTwo?.classList.add("left-full");
    dropdownLevelTwo?.classList.add("opacity-0");
    dropdownLevelTwo?.classList.add("invisible");

    try {
      await checkCanFocusTrap([dropdownLevelOne], function (trapContainer) {
        return (
          getComputedStyle(trapContainer).visibility !== "hidden" &&
          parseInt(getComputedStyle(trapContainer).left) === 0
        );
      });

      mobileNavLastFocusableElement = dropdownLevelOneLastFocusElement;
      const lastFocusedElement = node.querySelector<HTMLElement>(
        `.js-dropdown-level-1-link[data-level-two-id="${activeLevelTwoDropdown}"]`
      );
      lastFocusedElement.focus();
      activeLevelTwoDropdown = null;
    } catch (error) {
      console.error(error);
    }
  };

  // Setup Listeners
  node.addEventListener("keydown", handleMobileMenuKeyboardEvents);
  topNavHamburgerElement.addEventListener("click", openMobileDropdownHandler);
  topNavCloseElement.addEventListener("click", closeMobileDropdownHandler);

  dropdownLevelOneLinkElements.forEach((link) =>
    link.addEventListener("click", openMobileDropdownLevelTwoHandler)
  );
  dropdownLevelTwoCloseButtonElements.forEach((button) =>
    button.addEventListener("click", closeMobileDropdownLevelTwoHandler)
  );

  cartButtonElements.forEach((icon) => {
    icon.addEventListener("click", cartClickHandler);
  });

  context.on("cart:updated", (payload) => {
    if (payload.item_count > 0) {
      cartButtonElements.forEach((button) => {
        button.querySelector(".js-cart-bubble").classList.remove("hidden");
        button.querySelector(".js-cart-count").children[0].innerHTML =
          payload.item_count;
      });
    } else {
      cartButtonElements.forEach((button) => {
        button.querySelector(".js-cart-bubble").classList.add("hidden");
      });
    }
  });

  return () => {
    node.removeEventListener("keydown", handleMobileMenuKeyboardEvents);
    topNavHamburgerElement.removeEventListener(
      "click",
      openMobileDropdownHandler
    );
    topNavCloseElement.removeEventListener("click", closeMobileDropdownHandler);

    dropdownLevelOneLinkElements.forEach((link) =>
      link.removeEventListener("click", openMobileDropdownLevelTwoHandler)
    );
    dropdownLevelTwoCloseButtonElements.forEach((button) =>
      button.removeEventListener("click", closeMobileDropdownLevelTwoHandler)
    );
    cartButtonElements.forEach((button) => {
      button.removeEventListener("click", cartClickHandler);
    });
  };
});
